import { autoFocus } from "app/client/lib/domUtils";
import { dateFmt } from "app/client/lib/formatUtils";
import { ValidationGroup, Validator } from "app/client/lib/Validator";
import { AppModel, getHomeUrl } from "app/client/models/AppModel";
import { BillingModelImpl } from "app/client/models/BillingModel";
import { reportError } from "app/client/models/errors";
import { urlState } from "app/client/models/gristUrlState";
import * as css from "app/client/ui/BillingPageCss";
import { buildPlanCard, matchPlan, PlanContentModel } from "app/client/ui/PlanCard";
import { buildPlansPage, CREATE_TEAM_HEADER, PlansPageContent,
  SUB_HEADER_CREATE, SUB_HEADER_UPGRADE, UPGRADE_TEAM_HEADER } from "app/client/ui/PlansPage";
import { bigBasicButton, bigPrimaryButton, bigPrimaryButtonLink } from "app/client/ui2018/buttons";
import { mediaSmall, theme, vars } from "app/client/ui2018/cssVars";
import { icon } from "app/client/ui2018/icons";
import { loadingDots, loadingSpinner, watchPromise } from "app/client/ui2018/loaders";
import { confirmModal, IModalControl, modal } from "app/client/ui2018/modals";
import { BillingAPIImpl, ChangeSummary, PlanSelection } from "app/common/BillingAPI";
import { TEAM_FREE_PLAN } from "app/common/Features";
import { inlineStyle } from "app/common/gutil";
import { checkSubdomainValidity } from "app/common/orgNameUtils";
import { UserAPIImpl } from "app/common/UserAPI";

import { bundleChanges, Disposable, dom, DomContents, DomElementArg, DomMethod,
  makeTestId, Observable, styled, UseCB } from "grainjs";

const testId = makeTestId("test-productupgrade-");

/**
 * Product upgrade UI.
 *
 * Contains and exposes 3 elements:
 * - New site modal (modal shown after pressing new site button)
 * - Upgrade modal (modal shown after pressing upgrade button or link)
 * - Upgrade button that is shown in the top bar
 *   NOTE: upgrade button for free personal site is called "Create site"
 *
 * For Free personal site - there is no upgrade logic yet, so all upgrades are actually routed to new
 * site creation modal. For example Upgrade button for free personal org is called Create site and will
 * display create site modal instead of an upgrade.
 */

/**
 * Builds a new site modal.
 * @param owner Parent like object, when disposed modal will be closed.
 * @param options - Options
 * @param options.appModel - AppModel
 * @param options.plan - Current billing plans
 * @param options.onCreate - onCreate callback
 */
export async function buildNewSiteModal(owner: Disposable, options: {
  appModel: AppModel,
  plan?: PlanSelection,
  onCreate?: () => void
}): Promise<void> {
  const { onCreate } = options;
  const content: PlansPageContent = {
    header: "Create a new site",
    subHeader: SUB_HEADER_CREATE,
    footer: "Show all features https://www.getgrist.com/pricing/",
    reason: "create",
  };
  const model = new BillingModelImpl(options.appModel);
  content.plans = await model.readPlans(owner, options.plan);
  showModal(
    owner,
    () => dom.create(NewSiteModalContent, content, options.plan ?? null, onCreate),
    dom.cls(cssModalIndex.className),
  );
}

type Page = "plans" | "teamFreeSuccess" | "checkout";
class NewSiteModalContent extends Disposable {
  private _page = Observable.create<Page>(this, "plans");
  private _team = Observable.create(this, "");
  private _domain = Observable.create(this, "");
  private _selected = Observable.create<PlanContentModel | null>(this, null);

  constructor(
    private _content: PlansPageContent,
    selectedPlan: PlanSelection | null,
    private _onCreate?: (planName: string) => void) {
    super();

    if (!_content.plans) {
      throw new Error("Plans are not provided");
    }

    // Make sure each plan has a gristProduct.
    _content.plans.forEach((plan) => {
      if (!plan.gristProduct) {
        throw new Error(`Plan ${plan.priceId} does not have a gristProduct`);
      }
    });

    if (selectedPlan?.priceId || selectedPlan?.product) {
      const plan = _content.plans.find((p) => {
        return matchPlan(selectedPlan, p);
      });
      if (!plan) {
        throw new Error(`Plan ${selectedPlan.priceId || selectedPlan.product} not found`);
      }
      this._selected.set(plan);
      this._page.set("checkout");
    }
  }

  public buildDom() {
    const page = this._page;
    const team = this._team;
    const domain = this._domain;

    const onSelect = async (selected: PlanContentModel) => {
      bundleChanges(() => {
        this._selected.set(selected);
        page.set("checkout");
      });
      return null;
    };

    return dom.domComputed(this._page, (pageValue) => {
      switch (pageValue) {
        case "plans": return dom.create(buildPlansPage, this._content, onSelect);
        case "teamFreeSuccess": return buildFreeSiteConfirm({ domain: domain.get() });
        case "checkout": {
          const plan = this._selected.get();
          if (!plan) {
            throw new Error("Plan is not selected");
          }
          switch (plan.gristProduct) {
            case TEAM_FREE_PLAN:
              return buildTeamCheckoutPage({
                team,
                domain,
                create: () => this._createFree(),
                cancel: () => page.set("plans"),
                header: CREATE_TEAM_HEADER,
                buttonText: "Create site",
                plan,
              });
            default: return buildTeamCheckoutPage({
              team,
              domain,
              create: () => this._createProTeamSite(plan.priceId!),
              cancel: () => page.set("plans"),
              header: UPGRADE_TEAM_HEADER,
              buttonText: "Checkout",
              plan,
            });
          }
        }
      }
    });
  }

  private async _createProTeamSite(priceId: string, count?: number) {
    if (!priceId) {
      throw new Error("Plan is not selected");
    }
    const api = new BillingAPIImpl(getHomeUrl());
    try {
      const result = await api.createTeam(this._team.get(), this._domain.get(), {
        priceId,
        count,
      }, window.location.href);
      if (result.checkoutUrl ?? result.orgUrl) {
        window.location.href = result.checkoutUrl ?? result.orgUrl!;
      } else {
        throw new Error("Checkout URL is not provided");
      }
    } catch (err) {
      reportError(err as Error);
    }
  }

  private async _createFree() {
    const api = new BillingAPIImpl(getHomeUrl());
    try {
      const result = await api.createTeam(this._team.get(), this._domain.get(), { product: TEAM_FREE_PLAN });
      this._page.set("teamFreeSuccess");
      if (this._onCreate) {
        this._onCreate(TEAM_FREE_PLAN);
      }
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      } else {
        // site was created without checkout
      }
    } catch (err) {
      reportError(err as Error);
    }
  }
}

type UpgradePage = "plans" | "checkout" | "planLoader";
export async function buildUpgradeModal(owner: Disposable, options: {
  appModel: AppModel,
  reason?: "upgrade" | "renew",
  pickPlan?: PlanSelection,
  summary?: ChangeSummary | null,
}) {
  const summaryObs: Observable<ChangeSummary | null> = Observable.create(owner, options.summary || null);
  const selectedPlan: Observable<PlanContentModel | null> = Observable.create(owner, null);
  const page: Observable<UpgradePage> = Observable.create(owner, "plans");
  const api = new BillingAPIImpl(getHomeUrl());
  const plans = await new BillingModelImpl(options.appModel).readPlans(owner, options.pickPlan, options.reason);
  const current = options.reason !== "renew" ? {
    currentPlan: options.appModel.planName,
    currentPriceId: options.appModel.currentPriceId,
  } : undefined;

  const pageSetup: PlansPageContent = {
    ...current,
    reason: options.reason,
    plans,
    header: UPGRADE_TEAM_HEADER,
    subHeader: SUB_HEADER_UPGRADE,
    footer: "Show all features https://www.getgrist.com/pricing/",
  };

  const when = (pageName: UpgradePage) => (use: UseCB) => use(page) === pageName;
  let control: IModalControl;

  const onPlanClicked = async (plan: PlanContentModel) => {
    selectedPlan.set(plan);
    try {
      if (plan.gristProduct === TEAM_FREE_PLAN) {
        confirmDowngradeToTeamFree(control, options.appModel);
        return null;
      }

      const result = await api.confirmChange({
        product: plan.gristProduct,
        priceId: plan.priceId,
      });
      if ("checkoutUrl" in result) {
        window.location.href = result.checkoutUrl;
        summaryObs.set(null);
        page.set("plans");
      } else {
        summaryObs.set(result);
        page.set("checkout");
      }
    } catch (err) {
      reportError(err as Error);
      return null;
    }
  };

  const onConfirm = watchPromise(async () => {
    if (options.reason === "renew") {
      const result = await api.renewPlan({
        product: selectedPlan.get()!.gristProduct,
        priceId: selectedPlan.get()!.priceId,
      });
      if (result && "checkoutUrl" in result) {
        window.location.href = result.checkoutUrl;
        return;
      }
    } else {
      await api.changePlan({
        product: selectedPlan.get()!.gristProduct,
        priceId: selectedPlan.get()!.priceId,
        offerId: options.pickPlan?.offerId,
      });
    }
    // Refresh app model
    options.appModel.topAppModel.initialize();
    control.close();
  });

  // If we have plan selected, and it is in the plan list, we will show the summary right away.
  if (options.pickPlan) {
    const found = plans.find(p => matchPlan(options.pickPlan!, p));
    if (found) {
      selectedPlan.set(found);
      page.set("planLoader");
      onPlanClicked(found).catch(reportError);
    }
  } else if (options.summary) {
    const found = plans.find(p => matchPlan(options.pickPlan!, p));
    if (found) {
      selectedPlan.set(found);
    }
    page.set("checkout");
  }

  showModal(owner, (_: any, ctrl) => {
    control = ctrl;
    return [
      dom.maybe(when("checkout"), () =>
        cssWide(
          hspace("24px"),
          cssColumns(
            dom.maybe(selectedPlan, plan => buildPlanCard({ plan, single: true })),
            cssReview(
              cssInvoice(
                css.paymentRow(
                  dom.domComputed(use => use(summaryObs)?.type, type => [
                    type === "downgrade" ? cssHeader("Downgrade plan confirmation") :
                      type === "upgrade"   ? cssHeader("Review and confirm upgrade") :
                        cssHeader("Review and confirm plan"),
                  ]),
                ),
                dom.maybe(summaryObs, summary => [
                  summary.type !== "downgrade" ? buildInvoiceLine(
                    summary.productName,
                    `${summary.quantity} user${summary.quantity > 1 ? "s" : ""}`,
                    testId("user-count"),
                  ) : [
                    buildInvoiceLine(
                      `Your current plan will end on`,
                      dt(summary.regular.periodStart),
                      testId("end-on"),
                    ),
                    buildInvoiceLine(
                      dom("div",
                        dom("div", `On that day you will be subscribed to`),
                        dom("div", summary.productName),
                      ),
                      `${summary.quantity} user${summary.quantity > 1 ? "s" : ""}`,
                    ),
                  ],
                  buildInvoiceLine(
                    dom("div",
                      dom("div", `What you'll pay ${summary.interval}ly starting`),
                      dom("div", dt(summary.regular.periodStart)),
                    ),
                    summary.regular.tax !== undefined ?
                      summary.regular.total :
                      cssPriceCombined(
                        cssBold(
                          usd(summary.regular.total),
                        ),
                        cssGrayed(
                          "plus applicable tax",
                        ),
                      ),
                    testId("pay-starting"),
                  ),
                  !summary.regular.lines.length ? null :
                    cssDetails(
                      testId("details-regular"),
                      dom("summary", "Plan details"),
                      dom("div",
                        dom.forEach(summary.regular.lines,
                          line => buildInvoiceLine(cssBold(line.description), line.amount)),
                        horizontalLine(),
                        buildInvoiceLine(cssBold("Subtotal"), summary.regular.subTotal),
                        buildInvoiceLine(
                          "Tax",
                          summary.regular.tax === undefined ? "Applicable Tax Included" : summary.regular.tax,
                        ),
                        horizontalLine(),
                        buildInvoiceLine(cssBold("Total"), summary.regular.total),
                      ),
                    ),
                  !summary.invoice ? null : [
                    horizontalLine(),
                    buildInvoiceLine(
                      cssLeftText(
                        "Amount due ", summary.invoice.dueDate ? `on ${dt(summary.invoice.dueDate)}` : "today"),
                      cssBold(usd(summary.invoice.amountDue)),
                      testId("amount-due"),
                    ),
                    cssDetails(
                      testId("details-due"),
                      dom("summary", "Prorated changes"),
                      dom("div",
                        dom.forEach(summary.invoice.lines,
                          line => buildInvoiceLine(cssBold(line.description), line.amount)),
                        buildInvoiceLine(cssBold("Subtotal"), summary.invoice.subTotal),
                        buildInvoiceLine("Tax", summary.invoice.tax || 0),
                        horizontalLine(),
                        buildInvoiceLine(cssBold("Total"), summary.invoice.total),
                        horizontalLine(),
                        buildInvoiceLine("Applied balance", summary.invoice.appliedBalance),

                        buildInvoiceLine(
                          cssBold(
                            "Amount due " + (summary.invoice.dueDate ? `on ${dt(summary.invoice.dueDate)}` : "today"),
                          ),
                          summary.invoice.amountDue,
                        ),
                      ),
                    ),
                  ],
                ]),
              ),

              cssWarningWrapper(
                dom.show(use => use(selectedPlan)?.gristProduct === TEAM_FREE_PLAN),
                dom.style("margin-top", "18px"),
                dom("div",
                  testId("downgrade-confirm-modal"),
                  dom("div",
                    "Documents on free team plan are subject to the following limits." +
                    " Any documents in excess of these limits will be put in read-only mode.",
                  ),
                  dom("ul",
                    dom("li", { style: "margin-bottom: 0.6em" }, dom("strong", "5,000"), " rows per document"),
                    dom("li", { style: "margin-bottom: 0.6em" }, dom("strong", "10MB"), " max document size"),
                    dom("li", "API limit: ", dom("strong", "3k"), " calls/month/team"),
                  ),
                ),
              ),

              cssButtonsRow(
                bigBasicButton(
                  "Change plan",
                  dom.on("click", () => (summaryObs.set(null), page.set("plans"))),
                  testId("cancel")),
                bigPrimaryButton(
                  options.reason === "renew" ? "Renew plan" :
                    dom.text(use => planAction(use(summaryObs)?.type)),
                  dom.on("click", () => onConfirm().catch(reportError)),
                  testId("confirm"),
                  dom.prop("disabled", onConfirm.busy),
                  loadingDots(
                    dom.style("margin-left", "12px"),
                    inlineStyle("--dot-size", "8px"),
                    dom.show(onConfirm.busy),
                  ),
                ),
              ),
            ),
          ),
        ),
      ),
      dom.maybe(when("plans"), () => dom.create(buildPlansPage, pageSetup, p => onPlanClicked(p))),
      dom.maybe(when("planLoader"), () => cssWide(
        hspace("24px"),
        cssColumns(
          dom.maybe(selectedPlan, plan => buildPlanCard({ plan, single: true })),
          cssReview(
            dom("div",
              css.paymentRow(
                cssHeader("Review and confirm plan update"),
                dom.style("margin-bottom", "18px"),
              ),
            ),
            dom("div",
              dom.style("display", "grid"),
              dom.style("place-items", "center"),
              dom.style("flex-grow", "1"),
              loadingSpinner(),
            ),
          ),
        ),
      )),
    ];
  });
}

export { UpgradeButton } from "app/client/ui/UpgradeButton";

function confirmDowngradeToTeamFree(control: IModalControl, appModel: AppModel) {
  control.close();
  const api = new BillingAPIImpl(getHomeUrl());

  const downgradeToTeamFree = async () => {
    // Perform the downgrade operation.
    await api.changePlan({ product: TEAM_FREE_PLAN });
    // Refresh app model
    appModel.topAppModel.initialize();
  };

  showDowngradeToFreeModal(downgradeToTeamFree);
}

export function showDowngradeToFreeModal(action: () => Promise<void>) {
  confirmModal("Downgrade to Free Team Plan",
    "Downgrade",
    action,
    {
      explanation: (
        dom("div", testId("downgrade-confirm-modal"),
          dom("div",
            "Documents on free team plan are subject to the following limits." +
            " Any documents in excess of these limits will be put in read-only mode.",
          ),
          dom("ul",
            dom("li", { style: "margin-bottom: 0.6em" }, dom("strong", "5,000"), " rows per document"),
            dom("li", { style: "margin-bottom: 0.6em" }, dom("strong", "10MB"), " max document size"),
            dom("li", "API limit: ", dom("strong", "3k"), " calls/month/team"),
          ),
        )
      ),
    },
  );
}

export function showTeamUpgradeConfirmation(owner: Disposable) {
  showModal(owner, (_, ctrl) => buildTeamUpgradeConfirmation(ctrl));
}

function buildFreeSiteConfirm({
  domain,
}: {
  domain: string;
}) {
  return buildConfirm({
    buttonText: "Go to your site",
    header: "Team site created",
    link: urlState().setLinkUrl({ org: domain || undefined }),
  });
}

function buildTeamUpgradeConfirmation(ctrl: IModalControl) {
  return buildConfirm({
    buttonText: "Close",
    header: "Plan is upgraded",
    text: `Thank you! Enjoy premium features that bring your workflow to the next level.`,
    onClick: () => ctrl.close(),
  });
}

export function buildConfirm(options: {
  header: DomContents,
  text?: DomContents,
  link?: DomMethod<HTMLElement> | DomMethod<HTMLElement>[],
  buttonText: DomContents,
  onClick?: () => void
}) {
  return cssConfirmWrapper(
    cssSparks(),
    hspace("22px"),
    cssHeaderLine(options.header, testId("confirmation")),
    options.text ? dom("div", options.text) : null,
    hspace("40px"),
    options.link ? bigPrimaryButtonLink(options.link, options.buttonText, testId("confirmation-link")) : null,
    options.onClick ?
      bigPrimaryButton(options.buttonText, dom.on("click", options.onClick), testId("confirmation-button")) : null,
  );
}

function buildTeamCheckoutPage({
  team,
  domain,
  create,
  cancel,
  header,
  plan,
  buttonText,
}: {
  team: Observable<string>;
  domain: Observable<string>;
  create: () => any;
  cancel: () => any;
  header?: DomContents;
  plan: PlanContentModel;
  buttonText: string;
}) {
  const disabled = Observable.create(null, false);
  const group = new ValidationGroup();
  async function click() {
    disabled.set(true);
    try {
      if (!await group.validate()) {
        return;
      }
      await create();
    } finally {
      disabled.set(false);
    }
  }
  const clickOnEnter = dom.onKeyPress({
    Enter: () => click(),
  });
  return cssWide(
    cssHeaderLine(header),
    cssSubHeaderLine("Choose a name and subdomain for your team site"),
    hspace("24px"),
    cssColumns(
      buildPlanCard(
        { plan, single: true },
      ),
      cssSetup(
        cssPaymentLabel("Team name"),
        css.paymentRow(css.paymentField(css.billingInput(
          team,
          { onInput: true },
          autoFocus(),
          group.inputReset(),
          clickOnEnter,
          testId("name")))),
        dom.create(Validator, group, "Team name is required", () => !!team.get()),
        hspace("2em"),
        cssPaymentLabel("Team subdomain"),
        css.paymentRow(
          { style: "align-items: baseline" },
          css.paymentField(css.billingInput(
            domain, { onInput: true }, clickOnEnter, group.inputReset(), testId("domain"),
          )),
          css.paymentField({ style: "flex: 0 1 0; min-width: auto; margin-left: 5px" }, dom.text(".getgrist.com")),
        ),
        dom.create(Validator, group, "Domain name is required", () => !!domain.get()),
        dom.create(Validator, group, "Domain name is invalid", () => checkSubdomainValidity(domain.get())),
        dom.create(Validator, group, "Domain already in use", () => checkDomainAvailability(domain.get())),
        cssButtonsRow(
          bigBasicButton(
            "Change plan",
            dom.on("click", cancel),
            testId("cancel")),
          bigPrimaryButton(buttonText,
            dom.on("click", click),
            dom.prop("disabled", disabled),
            testId("confirm"),
          ),
        ),
      ),
    ),
  );
}

async function checkDomainAvailability(domainToCheck: string) {
  const api = new UserAPIImpl(getHomeUrl());
  const result = await api.checkDomain(domainToCheck);
  return result.valid && result.available;
}

function showModal(
  context: Disposable,
  content: (owner: Disposable, ctrl: IModalControl) => DomContents,
  ...args: DomElementArg[]
) {
  let control!: IModalControl;
  modal((ctrl, modalScope) => {
    control = ctrl;
    // When parent is being disposed and we are still visible, close the modal.
    context.onDispose(() => {
      // If the modal is already closed (disposed, do nothing)
      if (modalScope.isDisposed()) {
        return;
      }
      // If not, and parent is going away, close the modal.
      ctrl.close();
    });
    return [
      cssUpgradeModal.cls(""),
      cssCloseButton(testId("close-modal"), cssBigIcon("CrossBig"), dom.on("click", () => ctrl.close())),
      content(modalScope, ctrl),
    ];
  }, { backerDomArgs: args });
  return control;
}

function hspace(height: string) {
  return dom("div", { style: `height: ${height}` });
}

export const cssUpgradeModal = styled("div", `
  position: relative;
  @media ${mediaSmall} {
    & {
      width: 100%;
      min-width: unset;
      padding: 24px 16px;
    }
  }
`);

const cssConfirmWrapper = styled("div", `
  text-align: center;
`);

const cssSparks = styled("div", `
  height: 48px;
  width: 48px;
  background-image: var(--icon-Sparks);
  display: inline-block;
  background-repeat: no-repeat;
  &-small {
    height: 20px;
    width: 20px;
    background-size: cover;
  }
`);

const cssColumns = styled("div", `
  display: flex;
  gap: 60px;
  flex-wrap: wrap;
`);

const cssSetup = styled("div", `
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`);

const cssReview = styled(cssSetup, `
  font-size: ${vars.largeFontSize};
`);

const cssHeaderLine = styled("div", `
  text-align: center;
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 16px;
`);

const cssSubHeaderLine = styled("div", `
  text-align: center;
  margin-bottom: 7px;
`);

const cssPaymentLabel = styled(css.paymentLabel, `
  line-height: 1.5em;
  margin: 0px;
  margin-bottom: 0.3em;
`);

const cssWide = styled("div", `
  min-width: 760px;
  @media ${mediaSmall} {
    & {
      min-width: unset;
    }
  }
`);

const cssButtonsRow = styled("div", `
  display: flex;
  justify-content: flex-end;
  margin-top: auto;
  min-width: 250px;
  gap: 10px;
  flex-wrap: wrap;
  @media ${mediaSmall} {
    & {
      margin-top: 60px;
    }
  }
`);

export const cssCloseButton = styled("div", `
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 4px;
  border-radius: 4px;
  cursor: pointer;
  --icon-color: ${theme.modalCloseButtonFg};

  &:hover {
    background-color: ${theme.hover};
  }
`);

const cssBigIcon = styled(icon, `
  padding: 12px;
`);

const cssModalIndex = styled("div", `
  z-index: ${vars.pricingModalZIndex}
`);

const cssHeader = styled("div", `
  font-size: 20px;
  line-height: 24px;
  font-weight: 700;
  margin-bottom: 16px;
  text-align: center;
  color: ${theme.text};
`);

const cssText = styled("div", `
  font-weight: 400;
  color: ${theme.text};
`);

const cssLeftText = styled(cssText, `
  max-width: 264px;
`);

const cssRightText = styled("div", `
  margin-left: auto;
`);

const horizontalLine = styled("hr", `
  border: 1px solid ${theme.loginPageLine};
  flex-grow: 1;
`);

const cssWarningWrapper = styled("div", `
  display: flex;
  margin-top: 18px;
  margin-left: -10px;
  margin-right: -10px;
  padding: 10px;
  background: ${theme.menuBg};
  border-radius: 10px;
  margin-bottom: 18px;
  & > div {
    flex-basis: 0px;
    width: 0px;
    flex-grow: 1;
    flex-shrink: 1;
  }
`);

const cssDetails = styled("details", `
  & > summary {
    list-style: none;
    font-size: smaller;
    color: ${theme.linkHover};
    cursor: pointer;
  }

  &[open] > summary {
    color: inherit;
    margin-bottom: 16px;
  }

  &[open] > div {
    padding: 20px;
    border-radius: 8px;
    font-size: 14px;
    background: #f4f5f8;
  }
  &[open] > div > div {
    margin-bottom: 16px;
  }
`);

const cssBold = styled("span", `
  font-weight: 700;
`);

const cssPriceCombined = styled("div", `
  display: block;
  text-align: right;
`);

const cssGrayed = styled("div", `
  text-align: right;
  font-weight: 200;
  font-size: 11px;
`);

const cssInvoice = styled("div", `
  display: flex;
  flex-direction: column;
  gap: 12px;
`);

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const dt = (date: number) => dateFmt(date * 1000);
const usd = (amount: number) => currencyFormatter.format(Math.floor(amount) / 100);

function buildInvoiceLine(description: any, amount: any, ...rest: any[]) {
  return css.paymentRow(
    cssLeftText(description, testId("line-desc")),
    cssRightText(
      typeof amount === "number" ? usd(amount) : amount,
      testId("line-value"),
    ),
    testId("invoice-line"),
    ...rest,
  );
}

function planAction(type?: "downgrade" | "upgrade") {
  return type === "downgrade" ? "Schedule downgrade" :
    type === "upgrade" ? "Upgrade now" : "Change plan";
}
